import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useChats,
  useCreateChat,
  useSubmitFeedback,
  chatKeys,
  useMessages,
  type GetChatsResponse,
  type GetMessagesResponse,
} from "./useChatQuery";
import { useFetchClient } from "../../../API/index";
import { WEB_SEARCH_STEP_TYPE } from "../../Utils/dataNormalization";
import type {
  MessageResponse,
  StreamingAssistantMessage,
} from "../../Components/ChatPanel/ChatThread/ChatMessage/messageTypes";

const toAssistantMessageRows = (
  chat_uuid: string,
  assistantMessage: StreamingAssistantMessage | null
): MessageResponse[] => {
  if (!assistantMessage) {
    return [];
  }
  const { message_uuid, reasoning, answers, webSearchResults } =
    assistantMessage;
  return [
    ...(reasoning?.map((reasoningItem) => ({
      chat_id: chat_uuid,
      role: "assistant" as const,
      content: reasoningItem.content,
      content_type: "text" as const,
      message_uuid,
      step_type: reasoningItem.type,
    })) ?? []),
    // Same position and shape as the BE's persisted row.
    ...(webSearchResults?.length
      ? [
          {
            chat_id: chat_uuid,
            role: "assistant" as const,
            content: "",
            content_type: "text" as const,
            message_uuid,
            step_type: WEB_SEARCH_STEP_TYPE,
            web_search_results: webSearchResults,
          },
        ]
      : []),
    ...(answers?.map((answer) => ({
      chat_id: chat_uuid,
      role: "assistant" as const,
      content: answer.content,
      content_type: answer.contentType,
      message_uuid,
      step_type: "answer" as const,
      is_final: answer.isFinal,
    })) ?? []),
  ];
};

/**
 * Chat API Hook
 *
 * Provides a unified interface for chat operations:
 * - GET /chats - List of chats (via TanStack Query)
 * - POST /chats - Create a new chat
 * - POST /chats/{chatId}/messages - Send message with SSE streaming
 * - POST /chats/{chatId}/messages/{messageId}/feedback - Submit message feedback
 *
 * @example
 * ```tsx
 * const { chats, createChat, sendMessage, submitFeedback } = useChatAPI();
 *
 * // Create a new chat
 * const { chatId } = await createChat();
 *
 * // Send message with streaming
 * try {
 *   const { reader, abortController } = await sendMessage(chatId, "Hello!");
 *   // Read stream...
 * } catch (error) {
 *   // Handle connection failure
 * }
 *
 * // Submit feedback
 * submitFeedback({ chatId, messageId, value: "like" });
 * ```
 */
export const useChatAPI = () => {
  const queryClient = useQueryClient();
  const fetchClient = useFetchClient({ domain: "ai" });

  /**
   * Send a message to a chat and get the streaming reader
   *
   * Opens an SSE connection and returns the reader and signal.
   * Component is responsible for reading the stream and processing chunks.
   *
   * Errors (network failures, non-2xx responses, SSE timeouts, missing body)
   * propagate to the caller so they can drive the UI error flow and
   * downstream telemetry (e.g. `type: "sse_timeout"` from useFetchClient).
   *
   * @param chatId - The chat ID to send the message to
   * @param content - The message content to send
   * @returns Object with reader and abortController
   * @throws Error when the connection cannot be established
   *
   * @example
   * ```tsx
   * try {
   *   const { reader, abortController } = await sendMessage(chatId, "Hello!");
   *   // Component handles reading
   * } catch (error) {
   *   // Handle connection failure
   * }
   * ```
   */
  const sendMessage = useCallback(
    async (
      chatId: string,
      content: string
    ): Promise<{
      reader: ReadableStreamDefaultReader<string>;
      abortController: AbortController;
    }> => {
      // Create abort controller for this specific stream.
      const abortController = new AbortController();

      // POST message to endpoint with SSE. Errors are no longer swallowed
      // here — useFetchClient logs them (including the distinct
      // `type: "sse_timeout"` for TTFB aborts) and the caller is responsible
      // for surfacing the UI error state.
      const response = await fetchClient(`/chats/${chatId}/messages`, {
        method: "POST",
        headers: {
          Accept: "text/event-stream",
          "Cache-Control": "no-cache",
        },
        body: JSON.stringify({ content }),
        signal: abortController.signal,
      });

      // Check for response body
      if (!response.body) {
        throw new Error("Response body is null");
      }

      // Get reader with text decoding
      const reader = response.body
        .pipeThrough(new TextDecoderStream())
        .getReader();

      return {
        reader,
        abortController,
      };
    },
    [fetchClient]
  );

  /**
   * Callback to invalidate chats after streaming completes
   * Call this after your component finishes reading the stream
   */

  const onStreamComplete = useCallback(
    ({
      chat_uuid,
      userMessage,
      assistantMessage,
    }: {
      chat_uuid?: string;
      userMessage: {
        content: string;
        message_uuid: string;
      } | null;
      assistantMessage: StreamingAssistantMessage | null;
    }) => {
      if (!chat_uuid) {
        // TODO: add logging, no need to throw an error
        return;
      }
      queryClient.setQueryData(
        chatKeys.messages(chat_uuid),
        (old: GetMessagesResponse = { messages: [] }) => {
          return {
            messages: [
              ...(old.messages || []),
              ...(!!userMessage
                ? [
                    {
                      chat_id: chat_uuid,
                      role: "user",
                      content: userMessage.content,
                      content_type: "text",
                      message_uuid: userMessage.message_uuid,
                    },
                  ]
                : []),
              ...toAssistantMessageRows(chat_uuid, assistantMessage),
            ],
          };
        }
      );
    },
    [queryClient]
  );

  const onChatRenamed = useCallback(
    (chat_uuid: string, title: string) => {
      queryClient.setQueryData(chatKeys.chats, (old: GetChatsResponse) => {
        return {
          ...old,
          chats: old.chats.map((chat) =>
            chat.chat_uuid === chat_uuid ? { ...chat, title } : chat
          ),
        };
      });
    },
    [queryClient]
  );

  return {
    chats: useChats,
    messages: useMessages,
    createChat: useCreateChat,
    sendMessage,
    submitFeedback: useSubmitFeedback,
    onStreamComplete,
    onChatRenamed,
  };
};

export default useChatAPI;
